import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BulkSmsPlatformStatus } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { Badge, Button, Card, CheckboxField, SelectField, Spinner, TextField } from '../../components/ui';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';

const URL = '/notifications/platform/sms';
const KEY = ['platform', 'sms'] as const;

/** Platform settings: the BulkSMS account. The token fields are write-only: they show "Set" or "Not set" and are never filled in. */
export function PlatformSmsSection({ canEdit }: { canEdit: boolean }) {
  const q = useQuery({ queryKey: KEY, queryFn: () => api.get(URL, { schema: BulkSmsPlatformStatus }) });
  const qc = useQueryClient();
  const toast = useToast();
  const [provider, setProvider] = useState<BulkSmsPlatformStatus['provider']>('log');
  const [senderId, setSenderId] = useState('');
  const [safety, setSafety] = useState(true);
  const [tokenId, setTokenId] = useState('');
  const [tokenSecret, setTokenSecret] = useState('');
  useEffect(() => {
    if (!q.data) return;
    setProvider(q.data.provider);
    setSenderId(q.data.senderId);
    setSafety(q.data.safetyBypassesLimit);
  }, [q.data]);

  const save = useMutation({
    mutationFn: () =>
      api.put(URL, {
        body: {
          provider,
          senderId,
          safetyBypassesLimit: safety,
          ...(tokenId.trim() !== '' ? { tokenId: tokenId.trim() } : {}),
          ...(tokenSecret.trim() !== '' ? { tokenSecret: tokenSecret.trim() } : {}),
        },
        schema: BulkSmsPlatformStatus,
      }),
    onSuccess: async (r) => {
      setTokenId('');
      setTokenSecret('');
      toast(t('sms.platform.settings.saved'));
      qc.setQueryData(KEY, r);
    },
  });
  const errs = fieldErrors(save.error);
  const flag = (set: boolean) => <Badge tone={set ? 'good' : 'warn'}>{t(set ? 'sms.platform.settings.set' : 'sms.platform.settings.notSet')}</Badge>;

  return (
    <Card
      title={t('sms.platform.settings.title')}
      description={t('sms.platform.settings.help')}
      footer={
        canEdit ? (
          <Button variant="primary" disabled={save.isPending || !q.data} onClick={() => save.mutate()}>
            {t('common.save')}
          </Button>
        ) : undefined
      }
    >
      {q.isPending ? <Spinner /> : null}
      {q.isError ? <ErrorPanel error={q.error} onRetry={() => void q.refetch()} /> : null}
      <ErrorLine error={save.error && Object.keys(errs).length === 0 ? save.error : null} />
      {q.data ? (
        <form className="grid gap-4 sm:grid-cols-2" onSubmit={(e) => e.preventDefault()}>
          <SelectField label={t('sms.platform.settings.provider')} value={provider} disabled={!canEdit} onChange={(e) => setProvider(e.target.value as typeof provider)}>
            <option value="off">{t('sms.platform.settings.provider.off')}</option>
            <option value="log">{t('sms.platform.settings.provider.log')}</option>
            <option value="bulksms">{t('sms.platform.settings.provider.bulksms')}</option>
          </SelectField>
          <TextField
            label={t('sms.platform.settings.senderId')}
            hint={t('sms.platform.settings.senderIdHint')}
            maxLength={11}
            value={senderId}
            readOnly={!canEdit}
            error={errs.senderId}
            onChange={(e) => setSenderId(e.target.value)}
          />
          <div>
            <TextField
              label={t('sms.platform.settings.tokenId')}
              hint={t('sms.platform.settings.replaceHint')}
              autoComplete="off"
              value={tokenId}
              readOnly={!canEdit}
              onChange={(e) => setTokenId(e.target.value)}
            />
            <p className="mt-1 text-sm">{flag(q.data.tokenIdSet)}</p>
          </div>
          <div>
            <TextField
              label={t('sms.platform.settings.tokenSecret')}
              hint={t('sms.platform.settings.replaceHint')}
              type="password"
              autoComplete="new-password"
              value={tokenSecret}
              readOnly={!canEdit}
              onChange={(e) => setTokenSecret(e.target.value)}
            />
            <p className="mt-1 text-sm">{flag(q.data.tokenSecretSet)}</p>
          </div>
          <CheckboxField
            className="sm:col-span-2"
            label={t('sms.platform.settings.safety')}
            hint={t('sms.platform.settings.safetyHint')}
            checked={safety}
            disabled={!canEdit}
            onChange={(e) => setSafety(e.target.checked)}
          />
        </form>
      ) : null}
    </Card>
  );
}
